import { AvatarSizes, ChilizLogoRounded, useIntl } from '@capsicum/common';
import { Alert, BorderedSection, CopyToClipboard, Divider, Image, Text } from '@capsicum/common-web';
import { CheckIcon } from '@capsicum/common-web/icons';
import { type WalletConnectConfirmationActions, type WalletConnectConfirmationActionsPayload } from '@socios-blk/core';
import React, { type ReactNode, useCallback } from 'react';
import styled from 'styled-components';

import { DOMAIN_VERIFICATION_ALERT_TYPES } from '../constants';

type SessionConnectionModalProps = {
	payload: WalletConnectConfirmationActionsPayload[WalletConnectConfirmationActions.AUTH_DAPP];
};

const GOVERNANCE_ORIGIN = 'https://governance.chilizchain.com';

export const SessionConnectionModal = ({ payload }: SessionConnectionModalProps) => {
	const { $t } = useIntl();
	const { params, verifyContext, walletAddress } = payload;
	const { icons, url } = params.proposer.metadata;
	const icon = icons.length ? icons[0] : null;
	const isGovernance = url?.startsWith(GOVERNANCE_ORIGIN);

	const getDomainVerificationAlert = useCallback((validation: 'INVALID' | 'UNKNOWN' | 'VALID', isScam?: boolean) => {
		if (!validation || validation === 'VALID') return null;

		const alertType = isScam ? 'RISK' : validation;
		const { icon: Icon, subTitle, title, type } = DOMAIN_VERIFICATION_ALERT_TYPES[alertType];

		return (
			<S.DomainAlert
				alignIcon="flex-start"
				closable={false}
				icon={Icon}
				subTitle={$t(subTitle)}
				title={$t(title)}
				type={type}
			/>
		);
	}, []);

	const SectionRow = ({ icon, text }: { icon: ReactNode; text: string }) => (
		<S.Row>
			<Text variant="text3">{text}</Text>
			<S.IconWrapper>{icon}</S.IconWrapper>
		</S.Row>
	);

	return (
		<S.Wrapper>
			{icon ? (
				<S.AppIcon alt="dApp Logo" height={50} optimized src={icon} width={50} />
			) : isGovernance ? (
				<S.AppIcon alt="Chiliz Logo" height={48} src={ChilizLogoRounded} width={48} />
			) : (
				<S.LogoPlaceholder />
			)}
			<S.SecondaryText testID="session-connection-modal-origin-url" variant="text2">
				{url}
			</S.SecondaryText>
			<S.Title testID="session-connection-modal-title" variant="title5">
				{$t('ncWallet.walletConnect.connectionModal.modalTitle')}
			</S.Title>
			{getDomainVerificationAlert(verifyContext.verified.validation, verifyContext.verified.isScam)}
			<S.StyledBorderedSection
				testID="wallet-connect-connection-modal-address-section"
				title={$t('ncWallet.walletConnect.connectionModal.addressTitle')}
			>
				<CopyToClipboard
					label={$t('ncWallet.walletConnect.connectionModal.addressTitle')}
					testID="wallet-connect-auth-modal-wallet-address"
					value={walletAddress}
				/>
			</S.StyledBorderedSection>
			<S.StyledBorderedSection
				testID="wallet-connect-connection-modal-permissions-section"
				title={$t('ncWallet.walletConnect.connectionModal.permissionsTitle')}
				titleInsideSection
			>
				<SectionRow
					icon={<S.StyledCheckIcon height="24px" width="24px" />}
					text={$t('ncWallet.walletConnect.connectionModal.text1')}
				/>
				<Divider orientation="horizontal" />
				<SectionRow
					icon={<S.StyledCheckIcon height="24px" width="24px" />}
					text={$t('ncWallet.walletConnect.connectionModal.text2')}
				/>
			</S.StyledBorderedSection>
		</S.Wrapper>
	);
};

namespace S {
	export const AppIcon = styled(Image)`
		display: block;
		margin: 0 auto ${({ theme }) => theme.sizing.xsmall};
	`;

	export const Wrapper = styled.div`
		display: block;
	`;

	export const LogoPlaceholder = styled.div`
		display: block;
		height: ${() => `${AvatarSizes.L}px`};
		width: ${() => `${AvatarSizes.L}px`};
		margin: 0 auto ${({ theme }) => theme.sizing.xsmall};
		border-radius: 50%;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.subtle};
	`;

	export const Title = styled(Text)`
		display: block;
		text-align: center;
		margin-bottom: ${({ theme }) => theme.sizing.medium};
	`;

	export const SecondaryText = styled(Text)`
		color: ${({ theme }) => theme.colors.text.main.subtle};
		text-align: center;
		margin-bottom: ${({ theme }) => theme.sizing.xxxsmall};
	`;

	export const StyledBorderedSection = styled(BorderedSection)`
		margin-bottom: ${({ theme }) => theme.sizing.medium};
	`;

	export const Row = styled.div`
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		padding: ${({ theme }) => theme.sizing.xxsmall} 0;
	`;

	export const IconWrapper = styled.div`
		width: ${() => `${AvatarSizes.XXXS}px`};
		height: ${() => `${AvatarSizes.XXXS}px`};
		margin-left: ${({ theme }) => theme.sizing.xxxsmall};
	`;

	export const StyledCheckIcon = styled(CheckIcon)`
		color: ${({ theme }) => theme.colors.icons.brand.enabled};
	`;

	export const DomainAlert = styled(Alert)`
		margin-bottom: ${({ theme }) => theme.sizing.large};
	`;
}
